import { gql, useQuery } from "utils/urql";
import { HomepagePodcastsQuery, PodcastEpisodeFragment } from "types/api";
import floodlinesPromoImage from "assets/images/homepage-floodlines-promo.jpg";
import Image from "components/Image";
import SectionHeading from "./SectionHeading";
import styles from "./Podcasts.module.scss";

const hardcodedPodcasts = [
  {
    id: "experiment",
    kicker: "Listen to the Latest Episode: The Experiment",
    title: "Stories From an Unfinished Country",
    url: "/podcasts/experiment/",
    riverImage: {
      url:
        "https://cdn.theatlantic.com/assets/marketing/static-toolkit/wnyc-the-experiment/cover-homepage.jpg",
      altText: "The Experiment cover art",
    },
  },
  {
    id: "floodlines",
    kicker: "Prologue",
    title: "The Story of an Unnatural Disaster",
    url: "/podcasts/floodlines/",
    riverImage: {
      url: floodlinesPromoImage,
      altText: "Floodlines cover art",
    },
  },
  // Use a type assertion because typescript will complain about passing an object without
  // `__typename` to the PodcastEpisode component, even though it isn't needed and doesn't get
  // returned from GraphQL
] as PodcastEpisodeFragment[];

const query = gql`
  fragment podcastEpisode on PodcastEpisode {
    id
    url
    title
    kicker
    riverImage(width: 173, height: 173, sizes: ["1x", "2x"]) {
      url
      srcSet
      altText
    }
  }

  query HomepagePodcasts {
    activePodcastSeries {
      id
      displayName
      url
      river(first: 1) {
        edges {
          node {
            ...podcastEpisode
          }
        }
      }
    }
  }
`;

type PodcastEpisodeProps = PodcastEpisodeFragment & { analyticsIndex: number };

const PodcastEpisode = ({
  title,
  kicker,
  url,
  riverImage,
  analyticsIndex,
}: PodcastEpisodeProps) => {
  return (
    <article className={styles.episode}>
      {riverImage && (
        <a
          href={url}
          className={styles.image}
          data-action={`click link - podcasts - image ${analyticsIndex}`}
        >
          <Image
            src={riverImage.url}
            width={173}
            height={173}
            srcSet={riverImage.srcSet}
            alt={riverImage.altText}
            lazy={true}
          />
        </a>
      )}
      <div>
        {kicker && (
          <span
            className={styles.kicker}
            dangerouslySetInnerHTML={{ __html: kicker }}
          />
        )}
        <h3 className={styles.title}>
          <a
            href={url}
            className={styles.titleLink}
            dangerouslySetInnerHTML={{ __html: title }}
            data-action={`click link - podcasts - title ${analyticsIndex}`}
          />
        </h3>
      </div>
    </article>
  );
};

const Podcasts = () => {
  const [{ data }] = useQuery<HomepagePodcastsQuery>({ query });

  if (!data) return null;

  return (
    <section>
      <SectionHeading
        title="Podcasts"
        url="/podcasts/"
        analyticsSlug="podcasts"
        secondaryLinkText="see all"
      />
      <ul className={styles.ul}>
        {hardcodedPodcasts.map((podcast, i) => (
          <li key={podcast.id} className={styles.li}>
            <PodcastEpisode {...podcast} analyticsIndex={i + 1} />
          </li>
        ))}
        {data.activePodcastSeries.map((series, i) => {
          // The first node in this river will always be
          // a `PodcastEpisode`, even though typescript thinks
          // it might be a `BlogArticle` or another article type.
          // Use a type assertion to tell typescript we're right.
          const latestEpisode = series.river.edges[0]
            .node as PodcastEpisodeFragment;

          const analyticsIndex = hardcodedPodcasts.length + i + 1;
          return (
            <li key={latestEpisode.id} className={styles.li}>
              <PodcastEpisode
                {...latestEpisode}
                analyticsIndex={analyticsIndex}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default Podcasts;
